(() => {
  const STORAGE_KEY = 'mirightone:recently-viewed-products:v1';
  const MAX_PRODUCTS = 24;

  const readProducts = () => {
    try {
      const products = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      if (!Array.isArray(products)) return [];

      return products.filter(
        (product) =>
          product &&
          product.id &&
          product.handle &&
          product.url &&
          product.title &&
          Number.isFinite(Number(product.price))
      );
    } catch (_error) {
      return [];
    }
  };

  const writeProducts = (products) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(products.slice(0, MAX_PRODUCTS)));
    } catch (_error) {
      // Recently viewed products are optional when storage is unavailable.
    }
  };

  const recordProduct = (dataElement) => {
    if (dataElement.dataset.recentlyViewedRecorded === 'true') return;

    try {
      const product = JSON.parse(dataElement.textContent);
      if (!product?.id || !product?.handle || !product?.url || !product?.title) return;

      const products = readProducts().filter((item) => String(item.id) !== String(product.id));
      writeProducts([{ ...product, viewedAt: Date.now() }, ...products]);
      dataElement.dataset.recentlyViewedRecorded = 'true';
    } catch (_error) {
      // Ignore malformed or incomplete product payloads.
    }
  };

  const recordProducts = (root = document) => {
    root.querySelectorAll('[data-recently-viewed-product]').forEach(recordProduct);
  };

  if (!customElements.get('recently-viewed-products')) {
    customElements.define(
      'recently-viewed-products',
      class RecentlyViewedProducts extends HTMLElement {
        connectedCallback() {
          if (this.isInitialized) return;
          this.isInitialized = true;

          this.track = this.querySelector('[data-recently-viewed-track]');
          this.template = this.querySelector('[data-recently-viewed-template]');
          this.prev = this.querySelector('[data-recently-viewed-prev]');
          this.next = this.querySelector('[data-recently-viewed-next]');
          this.status = this.querySelector('[data-recently-viewed-status]');
          if (!this.track || !this.template) return;

          this.currentPage = 0;
          this.boundRefresh = () => this.refresh();
          this.boundResize = () => this.onResize();
          this.boundPointerMove = (event) => this.onPointerMove(event);
          this.boundPointerUp = () => this.onPointerUp();

          this.prev?.addEventListener('click', () => this.changePage(-1));
          this.next?.addEventListener('click', () => this.changePage(1));
          this.track.addEventListener('pointerdown', (event) => this.onPointerDown(event));
          this.track.addEventListener('scroll', () => this.onScroll());
          window.addEventListener('storage', this.boundRefresh);
          window.addEventListener('pageshow', this.boundRefresh);
          window.addEventListener('resize', this.boundResize);

          this.refresh();
        }

        disconnectedCallback() {
          this.cancelScrollAnimation();
          window.removeEventListener('storage', this.boundRefresh);
          window.removeEventListener('pageshow', this.boundRefresh);
          window.removeEventListener('resize', this.boundResize);
          window.removeEventListener('pointermove', this.boundPointerMove);
          window.removeEventListener('pointerup', this.boundPointerUp);
          window.removeEventListener('pointercancel', this.boundPointerUp);
        }

        refresh() {
          this.cancelScrollAnimation();
          const products = readProducts();
          this.track.replaceChildren(...products.map((product) => this.createCard(product)));
          this.productCount = products.length;
          this.hidden = this.productCount === 0;
          if (this.hidden) return;

          this.currentPage = 0;
          requestAnimationFrame(() => {
            this.updateMetrics();
            this.goToPage(0, false);
          });
        }

        createCard(product) {
          const fragment = this.template.content.cloneNode(true);
          const slide = fragment.querySelector('.recently-viewed-products__slide');
          const links = fragment.querySelectorAll('[data-product-link]');
          const title = fragment.querySelector('[data-product-title]');
          const image = fragment.querySelector('[data-product-image]');
          const placeholder = fragment.querySelector('[data-product-placeholder]');
          const cardInner = fragment.querySelector('[data-product-card-inner]');
          const soldOutBadge = fragment.querySelector('[data-sold-out-badge]');
          const price = fragment.querySelector('[data-product-price]');
          const regularPrice = fragment.querySelector('[data-regular-price]');
          const comparePrice = fragment.querySelector('[data-compare-price]');
          const salePrice = fragment.querySelector('[data-sale-price]');
          const rating = fragment.querySelector('[data-product-rating]');
          const action = fragment.querySelector('[data-product-action]');
          const productUrl = this.getProductUrl(product.url);

          links.forEach((link) => {
            link.href = productUrl;
            link.setAttribute('aria-label', product.title);
          });
          title.textContent = product.title;

          const aspectRatio = Number(product.image?.aspectRatio) || 1;
          cardInner.style.setProperty('--ratio-percent', `${100 / aspectRatio}%`);
          if (product.image?.src) {
            image.src = product.image.src;
            image.alt = product.image.alt || product.title;
            if (product.image.width) image.width = Number(product.image.width);
            if (product.image.height) image.height = Number(product.image.height);
            placeholder.hidden = true;
          } else {
            image.remove();
            placeholder.hidden = false;
          }

          const productPrice = Number(product.price) || 0;
          const productCompareAtPrice = Number(product.compareAtPrice) || 0;
          const formattedPrice = this.formatMoney(productPrice);
          const priceText = product.priceVaries
            ? (this.dataset.fromPriceTemplate || 'From [price]').replace('[price]', formattedPrice)
            : formattedPrice;
          const isOnSale = productCompareAtPrice > productPrice;

          price.classList.toggle('price--on-sale', isOnSale);
          price.classList.toggle('price--sold-out', product.available === false);
          regularPrice.textContent = priceText;
          salePrice.textContent = priceText;
          comparePrice.textContent = isOnSale ? this.formatMoney(productCompareAtPrice) : '';

          const judgemeBadge = typeof product.judgemeBadge === 'string' ? product.judgemeBadge.trim() : '';
          rating.dataset.id = product.id;
          rating.innerHTML = judgemeBadge;
          rating.hidden = !judgemeBadge;

          if (product.available === false) {
            const soldOutLabel = this.dataset.soldOutLabel || 'Sold out';
            soldOutBadge.hidden = false;
            action.textContent = soldOutLabel;
            action.removeAttribute('href');
            action.setAttribute('aria-disabled', 'true');
            action.setAttribute('aria-label', `${soldOutLabel} ${product.title}`);
          } else {
            const chooseOptionsLabel = this.dataset.chooseOptionsLabel || 'Choose options';
            soldOutBadge.hidden = true;
            action.textContent = chooseOptionsLabel;
            action.href = productUrl;
            action.removeAttribute('aria-disabled');
            action.setAttribute('aria-label', `${chooseOptionsLabel} ${product.title}`);
          }

          return slide;
        }

        getProductUrl(url) {
          try {
            const productUrl = new URL(url, window.location.origin);
            if (productUrl.origin !== window.location.origin) return '#';
            return `${productUrl.pathname}${productUrl.search}${productUrl.hash}`;
          } catch (_error) {
            return '#';
          }
        }

        formatMoney(cents) {
          try {
            return new Intl.NumberFormat(this.dataset.locale || undefined, {
              style: 'currency',
              currency: this.dataset.currency || 'USD',
            }).format(cents / 100);
          } catch (_error) {
            return `$${(cents / 100).toFixed(2)}`;
          }
        }

        getItemsPerPage() {
          if (window.matchMedia('(max-width: 749px)').matches) return 2;
          if (window.matchMedia('(max-width: 989px)').matches) return 3;
          return 6;
        }

        getCardStep() {
          const card = this.track.querySelector('.recently-viewed-products__slide');
          if (!card) return this.track.clientWidth;
          const trackStyle = getComputedStyle(this.track);
          const gap = Number.parseFloat(trackStyle.columnGap || trackStyle.gap || 0);
          return card.getBoundingClientRect().width + gap;
        }

        updateMetrics() {
          this.itemsPerPage = this.getItemsPerPage();
          this.cardStep = this.getCardStep();
          this.pageCount = Math.ceil(this.productCount / this.itemsPerPage);
          const showArrows = this.pageCount > 1;
          if (this.prev) this.prev.hidden = !showArrows;
          if (this.next) this.next.hidden = !showArrows;
          this.updateStatus();
        }

        changePage(direction) {
          if (this.pageCount <= 1) return;
          const nextPage = (this.currentPage + direction + this.pageCount) % this.pageCount;
          this.goToPage(nextPage);
        }

        goToPage(page, smooth = true) {
          this.currentPage = Math.max(0, Math.min(page, Math.max(0, this.pageCount - 1)));
          const left = this.getPageOffset(this.currentPage);
          if (smooth) {
            this.animateScrollTo(left);
          } else {
            this.cancelScrollAnimation();
            const previousBehavior = this.track.style.scrollBehavior;
            this.track.style.scrollBehavior = 'auto';
            this.track.scrollLeft = left;
            this.track.style.scrollBehavior = previousBehavior;
          }
          this.updateStatus();
        }

        animateScrollTo(left) {
          this.cancelScrollAnimation();

          const startLeft = this.track.scrollLeft;
          const distance = left - startLeft;
          if (Math.abs(distance) < 1) {
            this.track.scrollLeft = left;
            return;
          }

          this.isAnimating = true;
          this.previousScrollBehavior = this.track.style.scrollBehavior;
          this.previousScrollSnapType = this.track.style.scrollSnapType;
          this.track.style.scrollBehavior = 'auto';
          this.track.style.scrollSnapType = 'none';
          const startTime = performance.now();
          const duration = 320;

          const animate = (currentTime) => {
            const progress = Math.min(1, (currentTime - startTime) / duration);
            const easedProgress = 1 - Math.pow(1 - progress, 3);
            this.track.scrollLeft = startLeft + distance * easedProgress;

            if (progress < 1) {
              this.scrollAnimationFrame = requestAnimationFrame(animate);
              return;
            }

            this.track.scrollLeft = left;
            this.scrollAnimationFrame = null;
            this.restoreScrollStyles();
            this.isAnimating = false;
            this.updateStatus();
          };

          this.scrollAnimationFrame = requestAnimationFrame(animate);
        }

        cancelScrollAnimation() {
          if (this.scrollAnimationFrame) {
            cancelAnimationFrame(this.scrollAnimationFrame);
            this.scrollAnimationFrame = null;
          }
          if (this.isAnimating) {
            this.restoreScrollStyles();
            this.isAnimating = false;
          }
        }

        restoreScrollStyles() {
          this.track.style.scrollBehavior = this.previousScrollBehavior || '';
          this.track.style.scrollSnapType = this.previousScrollSnapType || '';
          this.previousScrollBehavior = null;
          this.previousScrollSnapType = null;
        }

        getPageOffset(page) {
          const maxScrollLeft = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
          const cardStep = this.cardStep || this.getCardStep();
          return Math.min(page * this.itemsPerPage * cardStep, maxScrollLeft);
        }

        getClosestPage(scrollLeft) {
          let closestPage = 0;
          let closestDistance = Number.POSITIVE_INFINITY;

          for (let page = 0; page < this.pageCount; page += 1) {
            const distance = Math.abs(scrollLeft - this.getPageOffset(page));
            if (distance < closestDistance) {
              closestPage = page;
              closestDistance = distance;
            }
          }

          return closestPage;
        }

        updateStatus() {
          if (!this.status || !this.pageCount) return;
          this.status.textContent = `Page ${this.currentPage + 1} of ${this.pageCount}`;
        }

        onScroll() {
          if (this.scrollFrame || this.isDragging || this.isAnimating || !this.cardStep || !this.itemsPerPage) return;
          this.scrollFrame = requestAnimationFrame(() => {
            this.scrollFrame = null;
            this.currentPage = this.getClosestPage(this.track.scrollLeft);
            this.updateStatus();
          });
        }

        onResize() {
          if (this.resizeFrame || this.hidden) return;
          this.resizeFrame = requestAnimationFrame(() => {
            this.resizeFrame = null;
            const firstVisibleProduct = (this.currentPage || 0) * (this.itemsPerPage || 1);
            this.updateMetrics();
            this.goToPage(Math.floor(firstVisibleProduct / this.itemsPerPage), false);
          });
        }

        onPointerDown(event) {
          if (event.target.closest?.('a, button') || event.pointerType === 'mouse' && event.button !== 0) return;

          this.cancelScrollAnimation();
          this.isDragging = true;
          this.didDrag = false;
          this.startX = event.clientX;
          this.lastX = event.clientX;
          this.pointerId = event.pointerId;
          this.track.classList.add('is-dragging');
          this.track.setPointerCapture?.(event.pointerId);
          window.addEventListener('pointermove', this.boundPointerMove);
          window.addEventListener('pointerup', this.boundPointerUp);
          window.addEventListener('pointercancel', this.boundPointerUp);
        }

        onPointerMove(event) {
          if (!this.isDragging) return;
          const distance = event.clientX - this.startX;
          if (Math.abs(distance) > 6) {
            this.didDrag = true;
            event.preventDefault();
          }

          this.track.scrollLeft -= event.clientX - this.lastX;
          this.lastX = event.clientX;
        }

        onPointerUp() {
          if (!this.isDragging) return;

          this.isDragging = false;
          this.track.classList.remove('is-dragging');
          if (this.pointerId !== null && this.track.hasPointerCapture?.(this.pointerId)) {
            this.track.releasePointerCapture(this.pointerId);
          }
          this.pointerId = null;
          window.removeEventListener('pointermove', this.boundPointerMove);
          window.removeEventListener('pointerup', this.boundPointerUp);
          window.removeEventListener('pointercancel', this.boundPointerUp);

          this.goToPage(this.getClosestPage(this.track.scrollLeft));
          if (!this.didDrag) return;
          this.track.classList.add('was-dragged');
          window.setTimeout(() => this.track.classList.remove('was-dragged'), 120);
          this.didDrag = false;
        }
      }
    );
  }

  const initialize = () => {
    recordProducts();
    document.querySelectorAll('recently-viewed-products').forEach((section) => section.refresh?.());
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => {
    recordProducts(event.target);
    event.target.querySelectorAll('recently-viewed-products').forEach((section) => section.refresh?.());
  });
})();
